import { useEffect, useRef, useState } from "react";
import type { ApiError } from "../../../api/client";
import { FailureAlert } from "../../../components/common/FailureAlert";
import { useTranslation } from "react-i18next";
import {
  CAlert,
  CButton,
  CForm,
  CSpinner,
  CFormLabel,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react-pro";
import {
  QUESTION_MAX_LENGTH,
  QUESTION_MIN_LENGTH,
  isRateLimited,
  RATE_WINDOW_MS,
  useAskStrategyQuestion,
  withMinimumDuration,
} from "../../../api/hooks/useTradingStrategyFeedback";
import type { TradingStrategyDto } from "../../../api/hooks/useTradingStrategies";

/** Long enough to read the confirmation, short enough not to feel stuck. */
const CONFIRMATION_MS = 5000;

interface AskQuestionModalProps {
  strategy: TradingStrategyDto;
  visible: boolean;
  onClose: () => void;
  /** Fired the moment the send succeeds, not when the confirmation finishes. */
  onSent: () => void;
  /** Fired when the server refuses on rate limit, with when it will accept again. */
  onRateLimited: (until: Date) => void;
  onAsked: () => void;
}

export function AskQuestionModal({
  strategy,
  visible,
  onClose,
  onSent,
  onRateLimited,
  onAsked,
}: AskQuestionModalProps) {
  const { t } = useTranslation("tradingStrategies");
  const [question, setQuestion] = useState("");
  const [failed, setFailed] = useState<{ reference?: string } | null>(null);
  const [slowDown, setSlowDown] = useState(false);
  const [sent, setSent] = useState(false);
  // Spans the padded send, unlike the mutation's own pending flag which clears as soon as the request
  // returns - which is exactly the moment that is too early to stop showing the spinner.
  const [submitting, setSubmitting] = useState(false);
  const ask = useAskStrategyQuestion();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Confirmed in place rather than by the modal vanishing - closing the moment it succeeds looks
  // indistinguishable from the form giving up. It closes itself once the message has been read.
  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const tooShort = question.trim().length < QUESTION_MIN_LENGTH;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFailed(null);
    setSlowDown(false);
    setSubmitting(true);
    try {
      await withMinimumDuration(
        ask.mutateAsync({ strategyId: strategy.id, question: question.trim() }),
      );
      setQuestion("");
      // Counted here rather than in the confirmation callback: closing the dialog early
      // with Cancel or the cross must not lose the fact that it was sent.
      onSent();
      setSent(true);
      closeTimer.current = setTimeout(onAsked, CONFIRMATION_MS);
    } catch (error) {
      if (isRateLimited(error)) {
        setSlowDown(true);
        onRateLimited(new Date(Date.now() + RATE_WINDOW_MS));
      } else {
        setFailed({ reference: (error as ApiError)?.traceId });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Wider and taller than a confirmation: this is the one field on the form and there is no telling
  // how much someone needs to say. A box that looks small asks a small question.
  return (
    <CModal visible={visible} onClose={onClose} alignment="center" size="lg">
      <CModalHeader>
        <CModalTitle>{t("feedback.question.title", { name: strategy.publicName })}</CModalTitle>
      </CModalHeader>
      {sent ? (
        <CModalBody>
          <CAlert color="success" className="mb-0">
            <div className="fw-semibold">{t("feedback.question.sentTitle")}</div>
            <div className="small">{t("feedback.question.sentBody")}</div>
          </CAlert>
        </CModalBody>
      ) : (
      <CForm onSubmit={handleSubmit}>
        <CModalBody>
          <CFormLabel htmlFor="strategy-question">
            {t("feedback.question.label")} <span className="text-danger">*</span>
          </CFormLabel>
          <CFormTextarea
            id="strategy-question"
            rows={7}
            value={question}
            maxLength={QUESTION_MAX_LENGTH}
            placeholder={t("feedback.question.placeholder")}
            onChange={(e) => setQuestion(e.target.value)}
          />
          <div className="d-flex justify-content-between gap-3 form-text">
            <span>{t("feedback.question.minimum", { count: QUESTION_MIN_LENGTH })}</span>
            {/* Green once the minimum is met, matching the rating form. */}
            <span
              className={tooShort ? undefined : "text-success"}
              style={{ whiteSpace: "nowrap" }}
            >
              {question.length} / {QUESTION_MAX_LENGTH}
            </span>
          </div>

          {/* One note rather than two paragraphs, and placed where it is read while deciding whether
              to send rather than while deciding what to write. The advice wording follows the
              brochure's contact page so the limit is stated the same way everywhere. */}
          <p className="text-body-secondary small mt-3 mb-0">
            {t("feedback.question.note")}
          </p>

          {failed && (
            <FailureAlert
              message={t("feedback.question.failed")}
              reference={failed.reference}
              className="small mt-3 mb-0 py-2"
            />
          )}

          {slowDown && (
            <CAlert color="warning" className="small mt-3 mb-0 py-2">
              {t("feedback.question.slowDown")}
            </CAlert>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={onClose} type="button">
            {t("common:buttons.cancel")}
          </CButton>
          <CButton color="primary" type="submit" disabled={tooShort || submitting}>
            {submitting ? (
              <>
                <CSpinner size="sm" className="me-2" aria-hidden="true" />
                {t("feedback.question.submitting")}
              </>
            ) : (
              t("feedback.question.submit")
            )}
          </CButton>
        </CModalFooter>
      </CForm>
      )}
    </CModal>
  );
}
